import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	isReadOnly as computeReadOnly,
	canManageDestinations as computeCanManageDestinations,
	canManageLifecycleAndDelivery as computeCanManageLifecycle,
} from "../../utilities/permissions";
import Toast from "../../components/toast";
import { concurrentChangeToast } from "../../components/toast/concurrentChange";
import { getCohortbyCohortID, updateCohort, createCohort, refreshModeledCohort, isConcurrentChange } from "../../services/cohort";
import { DeleteCohort } from "../../services/delete";
import BasicInput from "../../components/inputs/basic";
import { getTag } from "../../services/tag";
import { getSession } from "../../services/auth";
import PropensityThrottle from "../../components/builder/propensityThrottle";
import { scorePercent } from "../../components/builder/propensityThrottle/score";
import ModelAccuracyRate from "../../components/builder/modelAccuracyRate";
import PropTypes from "prop-types";
import Button from "../../components/inputs/button";
import { DestinationsModal } from "../../components/builder-v2/modals";
import * as CONST from "../../utilities/constants";
import { formatDv360SlotSuffix } from "../../utilities/dv360Slots";
import { isEquativMethodId } from "../../utilities/equativPrice";
import Select from "../../components/inputs/select";
import Loader from "../../components/misc/loader";
import Slider from "../../components/inputs/slider";
import AudienceHeader from "../../components/layout/audienceHeader";
import MetricsBar from "../../components/layout/metricsBar";
import BuilderCard from "../../components/layout/builderCard";
import Modal from "../../components/layout/modal";
import "./index.scss";

const similaritySliderValues = [60,65,70,75,80,85,90,95];
const PROPENSITY_INFO =
	"A probability score that reflects how closely a user resembles users in the seed set";
const ACCURACY_INFO =
	"How well the model distinguishes between users that look like the seed set and users that don't";

function Modeled({ editMode }) {
	const [cohort, setCohort] = useState(null);
    const [modelAccuracyRate, setModelAccuracyRate] = useState(null);
    const [propensityThrottle, setPropensityThrottle] = useState([]);
	// Where the chart puts its first and last score, for the slider to line up with.
	const [plotArea, setPlotArea] = useState(undefined);
	const params = useParams();
	const uniqueId = params.id ?? false ? params.id.split("-").join(" ") : null;
	const [toast, setToast] = useState(null);
	const [audienceName, setAudienceName] = useState("");
	const [audienceDescription, setAudienceDescription] = useState("");
	const [loading, setLoading] = useState(false);
	const [refreshLoading, setRefreshLoading] = useState(false);
    const [selectedScore, setSelectedScore] = useState(70);
    const navigate = useNavigate();
	const [activationMethods, setActivationMethods] = useState([]);
    const [ttl, setTtl] = useState(null);
	// The version this audience was loaded at, sent back on save so a save someone else made
	// in the meantime is refused rather than overwritten (ASB-4801).
	const [loadedVersion, setLoadedVersion] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [preReleaseModeEnabled, setPreReleaseModeEnabled] = useState(false);
    const [defaultTtl, setDefaultTtl] = useState(null);
	const [gamNetworkCodes, setGamNetworkCodes] = useState([]);
    const [gamNetworkSelectionMode, setGamNetworkSelectionMode] = useState('all')
    const [gamNetworkSubset, setGamNetworkSubset] = useState('all')
    const [destinationsModalOpen, setDestinationsModalOpen] = useState(false);
    const [readOnly, setReadOnly] = useState(false);
    // Replaces the `required` the old name input carried; AudienceHeader's has none.
    const [validationAttempted, setValidationAttempted] = useState(false);
    // Publisher Contributor (role 5) edits content but not destinations. See ASB-3200.
    const [canManageDestinations, setCanManageDestinations] = useState(false);
    // Delete and Archive are the same lifecycle role as destinations (ASB-3200); Copy is
    // gated on the role rather than on view mode, per ASB-4023.
    const [canManageLifecycle, setCanManageLifecycle] = useState(false);
    const [isActive, setIsActive] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [copyName, setCopyName] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showArchiveModal, setShowArchiveModal] = useState(false);

	useEffect(() => {
		// `cohort === null` renders the loader, so a path that never fetches must say so.
		// With the toast: the failed state is a bare frame, matching the signal page
		// (`signal/index.js:440`), so the toast is the only thing that explains it. Every
		// `setCohort(false)` on that page carries one, and these two paths are why this one
		// did not.
		if (uniqueId) {
			verifyAccess();
			return;
		}
		setCohort(false);
		setToast({
			message: "No audience id in the address, so there is nothing to open.",
			status: "Invalid",
			title: "Error",
		});
	}, []);

    useEffect(() => {
        const pageTitle = editMode ? "Edit Audience" : "View Audience";
        document.title = cohort ? (`${pageTitle} | ${cohort.name}`) : (`${pageTitle}`);
    }, [cohort]);

	const verifyAccess = async () => {
		const session = getSession();
		if (!session) {
			// No route guard on /audience-modeling/*, so this is reachable. Not "loading".
			setCohort(false);
			setToast({
				message: "Sign in to open this audience.",
				status: "Invalid",
				title: "Not signed in",
			});
			return;
		}

		const isReadOnlyUser = computeReadOnly(session.role_id);
		setReadOnly(isReadOnlyUser);
		setCanManageDestinations(computeCanManageDestinations(session.role_id));
		setCanManageLifecycle(computeCanManageLifecycle(session.role_id));
		// A read-only user reaching /audience-modeling/edit/:id directly -- a shared link, a
		// bookmark, browser history -- would otherwise get an editable name, description,
		// similarity slider and Update button, and only learn the role cannot save when the
		// API refuses the PUT. The UI must not offer what the endpoint rejects, so send them
		// to the view page. Returning before the fetch is the point: nothing populates the
		// editable form on the way past. Same shape as the signal page (ASB-3886).
		//
		// params.id, not uniqueId: uniqueId rewrites hyphens to spaces for the lookup, so
		// using it here would put %20 in the URL. This is a route swap of an id the router
		// already matched, so it round-trips exactly.
		if (editMode && isReadOnlyUser) {
			navigate(`/audience-modeling/view/${params.id}`, { replace: true });
			return;
		}
		try {
			const _cohort = await getCohortbyCohortID(uniqueId, localStorage.getItem("activeSilo"));

			if (_cohort.data.cohort_type != 'modeled') {
				setCohort(false);
				setToast({
					message: `${uniqueId} is not a valid modeled audience.`,
					status: "Invalid",
					title: "Error",
				});
				return;
			}
                const tagId = _cohort.data.tagId;
                const tag = await getTag(tagId);
                if (tag.data.integrations && tag.data.integrations.gam) {
                    setGamNetworkCodes(tag.data.integrations.gam.networkCodes ?? []);
                }    
                const settings = tag.data.publisherSettings ?? {};
                // Local as well as state: the `defaultTtl` this closure captured is the
                // first render's null, so a cohort with no `ttl` threw on `.value` and the
                // catch reported it as "No Audience was found".
                const resolvedDefaultTtl =
                    CONST.ttlOptions.find((el) => el.value === settings.defaultTtl) ??
                    CONST.ttlOptions.find((el) => el.value === 30);
                setDefaultTtl(resolvedDefaultTtl);
                setPreReleaseModeEnabled(CONST.preReleasePublishers.includes(tag.data.publisher_name));
                setIsAdmin(session.role_id === 1);
                setAudienceName(_cohort.data.name);
                setAudienceDescription(_cohort.data.description);
                setLoadedVersion(_cohort.data.version ?? null);
                setSelectedScore(_cohort.data.modeled_conditions.propensity_score ? Math.round(_cohort.data.modeled_conditions.propensity_score * 100) : 70);
                setCohort(_cohort.data);
                // A whole percent. The old `Math.round(x * 100) / 100 * 100` drew 28.999999999999996%
                // for 0.29, and a rate of 0 read as falsy -- "Model Generation Error" for a real,
                // if poor, result.
                const rate = _cohort.data.modeled_info?.model_accuracy_rate;
                setModelAccuracyRate(rate != null ? Math.round(rate * 100) : null);
                // Rows by whole percent, not float identity: 0.7 and 0.7000000001 are one score.
                // Ascending, as the slider's marks are: the chart draws rows in the order given,
                // and the two must agree index for index.
                // One row per whole percent, the first kept: two rows that round to the same score
                // would give the chart a tick the slider has no mark for.
                setPropensityThrottle((_cohort.data.modeled_info?.propensity_throttle ?? [])
                    .filter((value) => similaritySliderValues.includes(scorePercent(value.score)))
                    .filter((value, i, rows) =>
                        rows.findIndex((r) => scorePercent(r.score) === scorePercent(value.score)) === i
                    )
                    .sort((a, b) => scorePercent(a.score) - scorePercent(b.score)));
                setActivationMethods(_cohort.data.activation_methods ?? []);
                setIsActive(_cohort.data.active ?? true);
                setTtl(_cohort.data.ttl ?? resolvedDefaultTtl.value);
                setGamNetworkSelectionMode(_cohort.data.gamNetworkSelectionMode ?? "all")
                setGamNetworkSubset(_cohort.data.gamNetworkSubset ?? []);
		} catch (error) {
			setCohort(false);
			const message = `No Audience was found with the id: ${uniqueId}`;
			setToast({
				message: message,
				status: "Invalid",
				title: "Error",
			});
		}
	};

	const toastClose = () => {
		setToast(null);
	};

    const handleRefresh = () => {
        setRefreshLoading(true);
        refreshModeledCohort(uniqueId).then((response) => {
            response.json().then(() => {
                setRefreshLoading(false);
                navigate(`/audience-modeling/view/${uniqueId}`);
            });
        }).catch((error) => {
            setRefreshLoading(false);
            const errors = error.data?.errors ? Object.entries(error.data.errors) : false;
            const message = errors ? errors.map((e) => e[1]).join("\n") : (error.data?.message || error.message || "Failed to refresh modeled audience");
            setToast({
                message: message,
                status: "Invalid",
                title: errors ? error.data.message : "Error",
            });
        });
    };

    const handleUpdateDestinations = async (selectedDestinations, networkMode, networkSubset, equativPrice) => {
        setDestinationsModalOpen(false);
        setLoading(true);
        try {
            const response = await getCohortbyCohortID(uniqueId, localStorage.getItem("activeSilo"));
            const payload = {
                data: [{
                    ...response.data,
                    activation_methods: selectedDestinations.map((d) => ({
                        id: parseInt(d.id, 10),
                        name: d.name,
                        type: d.type,
                    })),
                    gamNetworkSelectionMode: networkMode,
                    gamNetworkSubset: networkMode === "subset" ? networkSubset : [],
                    ...(equativPrice != null && { equativPrice }),
                    // The version this page loaded, not the re-read's (ASB-4801).
                    ...(loadedVersion != null && { version: loadedVersion }),
                }],
            };
            const updated = await (
                await updateCohort(payload, uniqueId, localStorage.getItem("activeSilo"))
            ).json();
            if (updated?.dv360SlotError) {
                setToast({
                    message: `Destinations updated successfully, but ${updated.dv360SlotError}`,
                    status: "Info",
                    title: "DV360 slot not assigned",
                });
            } else {
                setToast({
                    message: `Destinations updated successfully${formatDv360SlotSuffix(updated?.dv360Slot)}`,
                    status: "Success",
                    title: "Success",
                });
            }
            // Set GAM state only after the save succeeds. On failure, the modal's
            // unsaved networks would otherwise appear in the next open.
            setGamNetworkSelectionMode(networkMode);
            setGamNetworkSubset(networkSubset);
            // The version this save left, not the refetch's below (ASB-4801).
            if (updated?.version != null) setLoadedVersion(updated.version);
            // Re-fetch rather than trust selectedDestinations: it is sourced from the generic
            // /activationMethods catalog ({id, name, type, label}), so it never carries the
            // per-destination details object (e.g. Equativ's details.equativ.price, DV360's
            // slot). Setting activationMethods from it directly strips that detail for as long
            // as the page stays open, so the next time the Destinations modal opens it prefills
            // as if this were a fresh activation. In its own try/catch, after the success
            // toast above: the update already succeeded by this point, so a refetch failure
            // here must not surface as "Failed to update destinations" — worst case is a
            // stale local mirror until the next full page load.
            // Unlike the signal page, this handler never navigates -- a destinations save
            // leaves you on the page in both modes, as in the V2 builder -- so the refetch
            // runs in edit mode too. Dropping it here would leave the stale mirror in place
            // for as long as the page stays open.
            try {
                const refreshed = await getCohortbyCohortID(uniqueId, localStorage.getItem("activeSilo"));
                setActivationMethods(refreshed.data.activation_methods ?? []);
            } catch (refreshError) {
                console.error("Failed to refresh activation methods after updating destinations:", refreshError);
                // Fall back to the modal's selection, preserving any details the generic
                // catalog stripped (Equativ price, DV360 slot). Without this, a refetch
                // failure leaves the pre-save list in place and the next save can undo this one.
                setActivationMethods((prev) =>
                    selectedDestinations.map((d) => {
                        const details = prev.find((p) => p.id === d.id)?.details;
                        // `prev` is the PRE-save list, so its price is the one this save just
                        // replaced. Carrying it over would prefill the modal with a price the
                        // server no longer holds -- the same stale-price reopen ASB-4045 exists
                        // to fix, reached by a different route. Match on id: equativPrice.js is
                        // explicit that the display name is not a contract.
                        if (equativPrice != null && isEquativMethodId(d.id)) {
                            return {
                                ...d,
                                details: { ...details, equativ: { ...details?.equativ, price: equativPrice } },
                            };
                        }
                        return details ? { ...d, details } : d;
                    })
                );
            }
        } catch (error) {
            if (isConcurrentChange(error)) {
                setToast(concurrentChangeToast(error));
                return;
            }
            setToast({
                message: error.data?.message || error.message || "Failed to update destinations",
                status: "Invalid",
                title: "Error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        if (!audienceName || !audienceName.trim()) {
            setValidationAttempted(true);
            return;
        }

        setLoading(true);
        const payload = { data: [{
            name: audienceName,
            description: audienceDescription,
            modeled_conditions: {
                propensity_score: selectedScore / 100.0
            },
            ttl: ttl,
            ...(loadedVersion != null && { version: loadedVersion }),
        }] };
        updateCohort(payload, uniqueId, localStorage.getItem("activeSilo")).then((r) => {
            r.json().then((r) => {
                // Until the view page re-reads, its actions must send the version this save left.
                if (r?.version != null) setLoadedVersion(r.version);
                setLoading(false);
                if (r.dv360SlotError) {
                    setToast({
                        message: `${r.message}, but ${r.dv360SlotError}`,
                        status: "Info",
                        title: "DV360 slot not assigned",
                    });
                } else {
                    setToast({
                        message: `${r.message}${formatDv360SlotSuffix(r.dv360Slot)}`,
                        status: "Success",
                        title: "Success",
                    });
                }
                navigate(`/audience-modeling/view/${uniqueId}`);
            });
        }).catch((error) => {
            setLoading(false);
            if (isConcurrentChange(error)) {
                setToast(concurrentChangeToast(error));
                return;
            }
            const errors = error.data?.errors ? Object.entries(error.data.errors) : false;
            const message = errors ? errors.map((e) => e[1]).join("\n") : (error.data?.message || error.message || "Failed to update modeled audience");
            setToast({
                message: message,
                status: "Invalid",
                title: errors ? error.data.message : "Error",
            });
        });
    }

	const compact = (n) => Intl.NumberFormat("en-Us", { notation: "compact" }).format(n);

	// Returns null rather than "Invalid Date" for anything unparseable. Date.parse of a
	// malformed string is NaN, and new Date(NaN).toLocaleDateString() renders the literal
	// text "Invalid Date" into the header; a non-string also threw on .replace. The caller
	// treats null as "no date to show".
	const formatDate = (date) => {
		if (typeof date !== "string") return null;
		const parsed = Date.parse(date.replace("PM", "").replace("AM", ""));
		if (Number.isNaN(parsed)) return null;
		return new Date(parsed).toLocaleDateString();
	};

	// Copy sends the Manager's request, not the signal page's. The ticket settled this: the
	// server ignores the copied modeled_info and starts a fresh model run, so a copy always
	// opens in the not-finished state until its own run completes. No special handling of
	// modeled_info here, and no schema_version rewrite -- that is the composite path only
	// (ASB-3712), and this payload is never composite.
	const handleCopy = async () => {
		if (!copyName.trim()) return;
		setActionLoading(true);
		try {
			const response = await getCohortbyCohortID(uniqueId, localStorage.getItem("activeSilo"));
			const copied = { ...response.data, cid: null, unique_id: null, name: copyName };
			if (copied.auto_generated) {
				copied.auto_generated = false;
				copied.tagId = parseInt(localStorage.getItem("activeSilo"), 10);
			}
			// A new audience starts its own versions (ASB-4801).
			delete copied.version;
			await createCohort({ data: [copied] });
			setToast({ message: "Audience copied successfully", status: "Success", title: "Success" });
			setShowCopyModal(false);
			setCopyName("");
		} catch (error) {
			setToast({
				message: error.data?.message || error.message || "Failed to copy audience",
				status: "Invalid",
				title: "Error",
			});
		} finally {
			setActionLoading(false);
		}
	};

	const handleDelete = async () => {
		setActionLoading(true);
		try {
			await DeleteCohort([uniqueId]);
			setToast({ message: "Audience deleted successfully", status: "Success", title: "Success" });
			setShowDeleteModal(false);
			setTimeout(() => navigate("/audience-manager"), 1500);
		} catch (error) {
			setToast({
				message: error.data?.message || error.message || "Failed to delete audience",
				status: "Invalid",
				title: "Error",
			});
		} finally {
			setActionLoading(false);
		}
	};

	const handleArchiveToggle = async () => {
		setActionLoading(true);
		try {
			const response = await getCohortbyCohortID(uniqueId, localStorage.getItem("activeSilo"));
			const saved = await (await updateCohort(
				{ data: [{
					...response.data,
					active: !isActive,
					// The version this page loaded, not the re-read's (ASB-4801).
					...(loadedVersion != null && { version: loadedVersion }),
				}] },
				uniqueId,
				localStorage.getItem("activeSilo")
			)).json();
			if (saved?.version != null) setLoadedVersion(saved.version);
			setIsActive(!isActive);
			setToast({
				message: `Audience ${isActive ? "archived" : "unarchived"} successfully`,
				status: "Success",
				title: "Success",
			});
			setShowArchiveModal(false);
		} catch (error) {
			if (isConcurrentChange(error)) {
				setShowArchiveModal(false);
				setToast(concurrentChangeToast(error));
				return;
			}
			setToast({
				message: error.data?.message || error.message || "Failed to update audience",
				status: "Invalid",
				title: "Error",
			});
		} finally {
			setActionLoading(false);
		}
	};

	// The signal page's columns with the modeled ones in place of the signal rule: the base
	// audience this was modelled from, and the propensity score the page is showing.
	const handleExport = () => {
		const rows = [
			["Date Created", "Unique ID", "Audience Name", "Audience Type", "Base Audience", "Propensity Score", "Status"],
			[
				// The audience's own creation date, as the Manager's export uses
				// (manager/index.js: date_created from cohort.createdon). `new Date()`
				// here would stamp every export with the day it was downloaded.
				formatDate(cohort?.createdon) ?? "",
				// cohort.unique_id, not `uniqueId`: the latter rewrites hyphens to spaces for
				// the lookup (line 40), so a hyphenated id exported as "abc def" in the cell
				// and in the filename while the header showed the real one.
				cohort?.unique_id ?? uniqueId,
				audienceName,
				"Modeled",
				// modeled_conditions carries composite_cohort_id and no name -- unlike
				// signal_conditions, which has base_cohort_name. Same `#id` fallback the
				// signal page uses when its name is absent, rather than a blank column.
				cohort?.modeled_conditions?.composite_cohort_id
					? `#${cohort.modeled_conditions.composite_cohort_id}`
					: "",
				`${selectedScore}+`,
				// The Manager labels a modeled audience by its model state before its
				// archive state (manager/index.js, cohortStatusLabel); an export that says
				// "Active" for a model that failed disagrees with the list it came from.
				!isActive
					? "Archived"
					: modelTraining
						? "Pending"
						: modelStatus === "error"
							? "Error"
							: "Active",
			],
		];
		// Quote every field so `Acme, Inc.` stays one column, and apostrophe-prefix a cell
		// opening with = + - or @, which Excel and Sheets execute. The name is free text and
		// Export is the one menu item no role gates.
		const csvCell = (v) => {
			const text = String(v ?? "");
			const safe = /^[=+\-@]/.test(text) ? `'${text}` : text;
			return `"${safe.replace(/"/g, '""')}"`;
		};
		const blob = new Blob([rows.map((r) => r.map(csvCell).join(",")).join("\n")], {
			type: "text/csv;charset=utf-8;",
		});
		const link = document.createElement("a");
		const url = URL.createObjectURL(blob);
		link.setAttribute("href", url);
		link.setAttribute("download", `cohort_${cohort?.unique_id ?? uniqueId}.csv`);
		link.style.visibility = "hidden";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		setToast({ message: "Export downloaded", status: "Success", title: "Success" });
	};

	const modelStatus = cohort && cohort.modeled_info && cohort.modeled_info.status ? cohort.modeled_info.status : "";
	const modelFinished = modelStatus === "finished";
	// Still being generated: nothing to show, as against nothing to report.
	const modelTraining = modelStatus === "created" || modelStatus === "started";
	const throttleAtScore = propensityThrottle.find((value) => scorePercent(value.score) === selectedScore);
	// The chart is drawn only for a finished model with curve rows (PropensityThrottle).
	const chartDrawn = modelFinished && propensityThrottle.length > 0;
	const sliderMarks =
		propensityThrottle.length > 0
			? similaritySliderValues.filter((val) =>
					propensityThrottle.some((e) => scorePercent(e.score) === val)
			  )
			: similaritySliderValues;
	// Optional at every hop: an audience whose metrics job has not run has no `metrics`, and
	// with no error boundary in this app an unguarded read blanks the page, not just the card.
	const served = cohort && cohort.metrics?.last30Days;
	const estimatedMetrics = Boolean(editMode || !served?.cohortLoads);
	// Refreshing a finished model leaves the PREVIOUS run's rows in propensity_throttle while
	// status returns to "started", so the curve is only trustworthy once finished. The served
	// numbers are deliberately not gated -- they are what the audience actually did.
	const curveAtScore = modelFinished ? throttleAtScore : undefined;
	const metricUsers = estimatedMetrics
		? curveAtScore
			? curveAtScore.unique_users_last_30_days
			: null
		: served?.uniqueUsers;
	const metricViews = estimatedMetrics
		? curveAtScore
			? curveAtScore.page_views_last_30_days
			: null
		: served?.cohortLoads;

	const nameInvalid = validationAttempted && !(audienceName && audienceName.trim());

	// `null` is the fetch in flight; `false` is a failed or wrong-type cohort, which the
	// toast below explains.
	if (cohort === null) {
		return (
			<div className="wrapper">
				<div className="as-page-loading">
					<Loader />
				</div>
			</div>
		);
	}

	return (
		<div className="wrapper">
			<form
				id="modeled-edit"
				onSubmit={handleSubmit}
				onKeyDown={(e) => {
					e.key === "Enter" && e.target.tagName.toLowerCase() != "textarea"
						? e.preventDefault()
						: null;
				}}
			>
				{cohort && (
					<>
						<AudienceHeader
							name={audienceName}
							onNameChange={editMode ? setAudienceName : undefined}
							// Edit only: AudienceHeader reuses it as the read-only name fallback.
							namePlaceholder={editMode ? "New Audience" : undefined}
							description={audienceDescription}
							onDescriptionChange={editMode ? setAudienceDescription : undefined}
							nameError={nameInvalid}
							error={nameInvalid ? "Name is required" : undefined}
							id={cohort.unique_id}
							destinationsCount={activationMethods.length}
							onConfigureDestinations={canManageDestinations ? () => setDestinationsModalOpen(true) : undefined}
							// View only, as on the V2 builder and signal pages. Gating is by ROLE,
							// not by mode: Copy follows isReadOnly (ASB-4023), Delete and Archive
							// follow the lifecycle role (ASB-3200), Export is for everyone because
							// it only writes out what the page already shows. Resulting menus --
							// admin/publisher admin/publisher user: all four; contributor: Copy +
							// Export; read-only: Export.
							menu={
								editMode
									? undefined
									: [
											...(!readOnly
												? [
														{
															label: "Copy",
															onClick: () => {
																setCopyName(`${audienceName} (Copy)`);
																setShowCopyModal(true);
															},
														},
												  ]
												: []),
											...(canManageLifecycle
												? [{ label: "Delete", onClick: () => setShowDeleteModal(true) }]
												: []),
											{ label: "Export", onClick: handleExport },
											...(canManageLifecycle
												? [
														{
															label: isActive ? "Archive" : "Unarchive",
															onClick: () => setShowArchiveModal(true),
														},
												  ]
												: []),
									  ]
							}
							actions={
								editMode ? (
									<>
										{/* TODO: Remove pre-release mode when model refresh can be used more widely */}
										{isAdmin && preReleaseModeEnabled && (
											<Button
												text="Refresh Model"
												type="button"
												style="light"
												onClick={() => handleRefresh()}
												disabled={modelTraining}
												loaded={refreshLoading}
											/>
										)}
										<Button
											text="Cancel"
											type="button"
											style="light"
											onClick={() => navigate("/audience-manager")}
											disabled={false}
											loaded={false}
										/>
										<Button
											text="Update"
											type="submit"
											style="dark"
											disabled={loading}
											loaded={loading}
										/>
									</>
								) : (
									<>
										{!readOnly && (
											<Button
												text="Edit"
												type="button"
												style="light"
												onClick={() => navigate(`/audience-modeling/edit/${uniqueId}`)}
												disabled={false}
												loaded={false}
											/>
										)}
										<Button
											text="Continue to Manager"
											type="button"
											style="dark"
											onClick={() => navigate("/audience-manager")}
											disabled={false}
											loaded={false}
										/>
									</>
								)
							}
						/>
						<MetricsBar
							metrics={[
								{ label: "Unique Users", value: metricUsers == null ? null : compact(metricUsers) },
								{ label: "Page Views", value: metricViews == null ? null : compact(metricViews) },
							]}
							qualifier={estimatedMetrics ? "Estimated" : "Actual"}
							qualifierTip={
								estimatedMetrics
									? "30-day estimate at the selected propensity score, from the last model run"
									: "Unique users and page views this audience has served over the last 30 days"
							}
							actions={
								!(modelTraining && estimatedMetrics) &&
								formatDate(cohort.modeled_info?.last_updated) ? (
									<span className="p4">
										Model updated on {formatDate(cohort.modeled_info.last_updated)}
									</span>
								) : null
							}
							emptyMessage={
								// Only where it explains an absence; beside real "Actual" numbers it
								// contradicts them.
								modelTraining && estimatedMetrics
									? "This model is still being generated, so its reach cannot be estimated yet."
									: null
							}
						/>
						<div className="container">
							<div className="container--inner modeled-cards">
								<div className="modeled-cards-grid">
									<div className="modeled-cards-main">
										{editMode ? (
											<BuilderCard
												// "Adjust" only when there is a slider to adjust.
												title={modelFinished ? "Adjust Propensity Score" : "Propensity Score"}
												info={PROPENSITY_INFO}
											>
												<PropensityThrottle
													data={propensityThrottle}
													status={modelStatus}
													selectedScore={selectedScore}
													mode="edit"
													// The slider's 60+ … 95+ labels sit under these ticks and serve as
													// the axis; the chart's own would repeat the score (Jose, 10-01).
													xAxisLabels={false}
													// Y values drawn as standard. For the viewer-controlled version,
													// pass `valuesToggle` (with showYAxisValues as its starting state);
													// drop both for the mockups' bare axis. Same in view mode below.
													showYAxisValues
													// Click a score on the chart to pick it; the slider follows.
													onSelectScore={modelFinished ? setSelectedScore : undefined}
													onPlotArea={setPlotArea}
												/>
												{/* Only for a finished model: generating or failed, there is
												    nothing to tune and a dead slider is noise beside the card's
												    message (Jose, 10-01). Update still sends the saved score. */}
												{modelFinished && (
													<Slider
														name="propensity-score"
														value={selectedScore}
														marks={sliderMarks}
														formatLabel={(v) => `${v}+`}
														// No bubble: the bold mark label already names the score.
														showValue={false}
														onChange={(newValue) => setSelectedScore(newValue)}
														tone="primary"
														markLabels
														// Each mark under its chart tick (criterion 9). Only while the
														// chart is drawn; otherwise there is nothing to line up with.
														markInset={chartDrawn ? plotArea : undefined}
														// The chart's dashed marker, continued down to the thumb.
														valueStem={chartDrawn}
													/>
												)}
											</BuilderCard>
										) : (
											<BuilderCard
												title="Propensity Score"
												info={PROPENSITY_INFO}
											>
												{/* Option B: view mode gains the reach curve the page never had,
												    with the saved score marked and the band above it shaded.
												    PropensityThrottle renders its own "no data" state while a
												    model is still generating, which is the same rule the
												    estimated numbers follow. */}
												<PropensityThrottle
													data={propensityThrottle}
													status={modelStatus}
													selectedScore={selectedScore}
													mode="view"
													showYAxisValues
													// Pending or failed, the card carries only its message.
													summary={
														modelFinished ? (
															<p className="modeled-score-value">{selectedScore}+</p>
														) : undefined
													}
												/>
												{chartDrawn && (
													<p className="p4 modeled-view-curve-caption">
														Reach at each score, from the last model run
													</p>
												)}
											</BuilderCard>
										)}
									</div>
									<div className="modeled-cards-side">
										<BuilderCard
											title="Model Accuracy Rate"
											info={ACCURACY_INFO}
										>
											<ModelAccuracyRate
												// Gated like the curve and the metrics: a refresh leaves the
												// PREVIOUS run's rate in modeled_info while status goes back to
												// "started", and the card would present it as current. Null is
												// what reaches the card's own Pending state.
												value={modelFinished ? modelAccuracyRate : null}
												status={modelStatus}
											/>
										</BuilderCard>
									</div>
								</div>
								{/* Option B: TTL full width below both cards, drawn as the V2 builder
								    draws it (AudienceBuilderV2.js:2205): a plain section, not a card. */}
								{editMode ? (
									<div className="modeled-ttl-section">
										<Select
											name="ttl"
											label="Time to Live"
											info={`Amount of time to keep users in the audience.${defaultTtl ? ` Defaults to ${defaultTtl.label}.` : ""}`}
											selected={
												CONST.ttlOptions.find((opt) => opt.value === ttl) ||
												// A stored ttl the options list does not carry (45, say, set
												// through the API) must still show its own value. Falling
												// through to defaultTtl would display the publisher default
												// while state held the real number -- and view mode, which
												// synthesises the same label below, would disagree with the
												// editor about the same audience.
												(ttl != null ? { value: ttl, label: `${ttl} days` } : defaultTtl)
											}
											onSubmit={(option) => setTtl(option.value)}
											options={CONST.ttlOptions}
											required={false}
											disabled={false}
											search={true}
											reset={true}
											defaultValue={defaultTtl}
										/>
									</div>
								) : (
									<div className="option-item">
										<span className="option-label">Time to Live:</span>
										<span className="option-value">
											{CONST.ttlOptions.filter((el) => el.value === ttl)[0]
												? CONST.ttlOptions.filter((el) => el.value === ttl)[0].label
												: `${ttl} days`}
										</span>
									</div>
								)}
							</div>
						</div>
					</>
				)}
			</form>
			<DestinationsModal
				isOpen={destinationsModalOpen}
				onClose={() => setDestinationsModalOpen(false)}
				onConfirm={handleUpdateDestinations}
				saveButtonText="Update Destinations"
				loading={loading}
				initialSelected={activationMethods}
				gamNetworkCodes={gamNetworkCodes}
				initialGamNetworkMode={gamNetworkSelectionMode}
				initialGamNetworkSubset={gamNetworkSubset}
			/>
			{/* The shared shell from ASB-3953 rather than the hand-built overlay the signal
			    page still uses: that one has no Escape, no focus trap and no focus return,
			    and its styling lives in AudienceBuilderV2.scss, so it is styled here only
			    because this page imports DestinationsModal from that tree. */}
			<Modal
				open={showCopyModal}
				title="Copy Audience"
				onClose={() => [setShowCopyModal(false), setCopyName("")]}
				footer={
					<>
						<Button
							text="Cancel"
							type="button"
							style="light"
							onClick={() => [setShowCopyModal(false), setCopyName("")]}
							disabled={actionLoading}
							loaded={false}
						/>
						<Button
							text="Copy"
							type="button"
							style="dark"
							onClick={handleCopy}
							disabled={!copyName.trim() || actionLoading}
							loaded={actionLoading}
						/>
					</>
				}
			>
				<p>Enter a name for the new audience:</p>
				<BasicInput
					name="copyName"
					customClasses="copy-name-input"
					required={true}
					disabled={false}
					placeholder="New audience name"
					value={copyName}
					onInput={(e) => setCopyName(e.target.value)}
				/>
				{/* A copy never inherits the source's results: the server ignores the copied
				    modeled_info and starts a fresh run. */}
				<p className="p4">The copy starts its own model run, so its results begin empty.</p>
			</Modal>

			<Modal
				open={showDeleteModal}
				title="Delete Audience"
				onClose={() => setShowDeleteModal(false)}
				footer={
					<>
						<Button
							text="Cancel"
							type="button"
							style="light"
							onClick={() => setShowDeleteModal(false)}
							disabled={actionLoading}
							loaded={false}
						/>
						<Button
							text="Delete"
							type="button"
							style="dark"
							onClick={handleDelete}
							disabled={actionLoading}
							loaded={actionLoading}
						/>
					</>
				}
			>
				<p>Are you sure you want to permanently delete this audience?</p>
				<p className="audience-name-display">{audienceName}</p>
			</Modal>

			<Modal
				open={showArchiveModal}
				title={`${isActive ? "Archive" : "Unarchive"} Audience`}
				onClose={() => setShowArchiveModal(false)}
				footer={
					<>
						<Button
							text="Cancel"
							type="button"
							style="light"
							onClick={() => setShowArchiveModal(false)}
							disabled={actionLoading}
							loaded={false}
						/>
						<Button
							text={isActive ? "Archive" : "Unarchive"}
							type="button"
							style="dark"
							onClick={handleArchiveToggle}
							disabled={actionLoading}
							loaded={actionLoading}
						/>
					</>
				}
			>
				<p>Are you sure you want to {isActive ? "archive" : "unarchive"} this audience?</p>
			</Modal>

			{toast && (
				<Toast
					// A new toast is a new instance: an earlier toast's auto-hide timer must not
					// close a refusal that is meant to stay (ASB-4801).
					key={`${toast.title}|${toast.message}`}
					status={toast.status}
					message={toast.message}
					title={toast.title}
					callback={() => toastClose()}
					autoHide={toast.autoHide ?? true}
					action={toast.action}
				/>
			)}
		</div>
	);
}

function ModeledEdit({ }) {
    return (<Modeled editMode={true} />)
}

function ModeledView({ }) {
    return (<Modeled editMode={false} />)
}

Modeled.propTypes = {
    editMode: PropTypes.bool.isRequired,
}

ModeledEdit.propTypes = {

}

ModeledView.propTypes = {
    
}

export {ModeledEdit, ModeledView};